import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { StandardizedStatementPreview } from '@interfaces/accounting-data-standardization/responses/standardized-statement-preview.response';
import { Option } from '@interfaces/common/option';
import { DFPService } from '@services/http/dfp/dfp.service';
import { DfpSourceDocumentPreviewService } from '../../services/dfp-source-document-preview.service';
import { Permissions } from '@app/core/guard/Permissions';
import { dfpHistoricalBalanceSheetResponse } from '@interfaces/dfp/response/dfpHistoricalBalanceSheetResponse';
import { dfpHistoricalIncomeStatementResponse } from '@interfaces/dfp/response/dfpIncomeStatementResponse';
import {
  DfpStatementKind,
  dfpSourceDocumentsResponse,
} from '@interfaces/dfp/response/dfpSourceDocumentsResponse';
import { PermissionsService } from '@services/helpers/permissions.service';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultDivideByThousandAndFormatPipe } from '@shared/pipes/vault-divide-by-thousand-and-format.pipe';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import { DFP_SCALE_TITLE_NOTE, DfpValueScale } from '../../models/dfp-value-scale.model';
import {
  originalStatementDocumentOptions,
  originalStatementDocumentsFor,
  sortOriginalStatementDocuments,
} from '../../utils/original-statement-documents.util';
import { DfpPanelComponent } from '../dfp-panel/dfp-panel.component';
import { OriginalStatementComponent } from '../original-statement/original-statement.component';
import { TableBalanceSheetComponent } from '../table-balance-sheet/table-balance-sheet.component';
import { TableIncomedStatementComponent } from '../table-incomed-statement/table-incomed-statement.component';

type ComparedStatement = 'income' | 'balance';

/**
 * Comparação "visão Legatus × Visão Cliente" (card 3614, opção C do protótipo `comparar-visao-legatus-e-visao-cliente`):
 * o botão "Comparar com o cliente" na barra de filtros abre uma janela grande com o demonstrativo padronizado à esquerda
 * e o do documento do cliente à direita, e um seletor DRE | Balanço. A tela de trás não muda.
 *
 * As duas colunas ficam no mesmo conteúdo rolável da janela, então rolam juntas sem código de sincronia. A tabela da
 * esquerda é a própria tabela da tela com `availablePeriods` nulo, o modo só leitura (sem seletor de visão, sem "+" e
 * sem menu de coluna), o mesmo da aba Visualização da Padronização.
 */
@Component({
  selector: 'app-statement-comparison',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    DfpPanelComponent,
    OriginalStatementComponent,
    TableIncomedStatementComponent,
    TableBalanceSheetComponent,
  ],
  templateUrl: './statement-comparison.component.html',
  styleUrls: ['./statement-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementComparisonComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  readonly companyId = input<string | null>(null);
  readonly companyName = input<string>('');
  readonly sourceDocuments = input<dfpSourceDocumentsResponse | null>(null);
  readonly valueScale = input<DfpValueScale>('original');
  readonly showAnalysisColumns = input<{ [key: string]: boolean }>({ av: false });
  /** Linhas da tela, só para saber se há DRE e Balanço para comparar (o lado esquerdo vem do documento). */
  readonly incomeData = input<dfpHistoricalIncomeStatementResponse[]>([]);
  readonly balanceData = input<dfpHistoricalBalanceSheetResponse[]>([]);

  private readonly dfpService = inject(DFPService);
  private readonly preview = inject(DfpSourceDocumentPreviewService);
  private readonly destroyRef = inject(DestroyRef);

  /** Permissão da Visão Cliente, a mesma que o backend confere. Começa `false`. */
  private readonly canView = toSignal(
    inject(PermissionsService).hasPermission(Permissions.DfpOriginalStatement.VIEW),
    { initialValue: false }
  );

  readonly statement = signal<ComparedStatement>('income');

  /** As tabelas da janela só montam depois da primeira abertura: fechada, a tela não carrega uma segunda DRE. */
  readonly opened = signal(false);

  readonly incomeDocuments = computed(() =>
    originalStatementDocumentsFor(this.sourceDocuments(), DfpStatementKind.IncomeStatement)
  );
  readonly balanceDocuments = computed(() =>
    originalStatementDocumentsFor(this.sourceDocuments(), DfpStatementKind.BalanceSheet)
  );

  /** O botão só aparece com a permissão e com documento lido em pelo menos um dos demonstrativos com dados na tela. */
  readonly canCompare = computed(
    () =>
      this.canView() &&
      ((this.incomeData().length > 0 && this.incomeDocuments().length > 0) ||
        (this.balanceData().length > 0 && this.balanceDocuments().length > 0))
  );

  readonly canCompareIncome = computed(
    () => this.incomeData().length > 0 && this.incomeDocuments().length > 0
  );
  readonly canCompareBalance = computed(
    () => this.balanceData().length > 0 && this.balanceDocuments().length > 0
  );

  readonly kind = computed(() =>
    this.statement() === 'income' ? DfpStatementKind.IncomeStatement : DfpStatementKind.BalanceSheet
  );
  readonly documents = computed(() =>
    this.statement() === 'income' ? this.incomeDocuments() : this.balanceDocuments()
  );
  readonly scaleNote = computed(() => DFP_SCALE_TITLE_NOTE[this.valueScale()]);

  /** Documento escolhido no select do topo, por demonstrativo (trocar DRE | Balanço não perde a escolha do outro). */
  private readonly chosenIds = signal<Record<ComparedStatement, string | null>>({
    income: null,
    balance: null,
  });

  /**
   * Documento comparado: o escolhido, se ainda estiver na lista; senão o que cobre o ano mais recente. Vale para os dois
   * lados: à esquerda o padronizado DELE (não o consolidado da tela, que pode vir de outro documento em algum ano), à
   * direita como foi lido.
   */
  readonly selectedId = computed<string | null>(() => {
    const documents = this.documents();
    const chosen = this.chosenIds()[this.statement()];
    if (chosen && documents.some(document => document.standardizationDocumentId === chosen)) {
      return chosen;
    }
    return sortOriginalStatementDocuments(documents)[0]?.standardizationDocumentId ?? null;
  });

  readonly documentOptions = computed<Option[]>(() =>
    originalStatementDocumentOptions(this.documents(), document =>
      this.preview.displayName(document)
    )
  );

  /** O padronizado do documento comparado (prévia do De/Para dele). */
  readonly standardized = signal<StandardizedStatementPreview | null>(null);
  readonly standardizedState = signal<'idle' | 'loading' | 'error' | 'ready'>('idle');

  constructor() {
    // Recarrega o padronizado quando muda o documento, o demonstrativo ou a empresa, com a janela já aberta.
    effect(() => {
      const opened = this.opened();
      const extractionId = this.selectedId();
      const kind = this.kind();
      const companyId = this.companyId();
      untracked(() => {
        if (opened) this.loadStandardized(companyId, kind, extractionId);
      });
    });
  }

  selectDocument(extractionId: string | null): void {
    if (!extractionId) return;
    this.chosenIds.update(ids => ({ ...ids, [this.statement()]: extractionId }));
  }

  private loadStandardized(
    companyId: string | null,
    kind: DfpStatementKind,
    extractionId: string | null
  ): void {
    this.standardized.set(null);
    if (!companyId || !extractionId) {
      this.standardizedState.set('idle');
      return;
    }

    this.standardizedState.set('loading');
    this.dfpService
      .getOriginalStatementStandardized(companyId, kind, extractionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: preview => {
          // Resposta de um documento que a pessoa já trocou: descarta.
          if (this.selectedId() !== extractionId) return;
          this.standardized.set(preview);
          this.standardizedState.set(preview ? 'ready' : 'error');
        },
        error: () => {
          if (this.selectedId() !== extractionId) return;
          this.standardizedState.set('error');
        },
      });
  }

  private readonly currencyPipe = new VaultFormatCurrencyPipe();
  private readonly thousandsPipe = new VaultDivideByThousandAndFormatPipe();

  /** Dinheiro na escala escolhida na tela, igual à tabela padronizada (`TableAbstractComponent.formatValue`). */
  readonly formatValue = (value: number | null): string =>
    this.valueScale() === 'thousands'
      ? this.thousandsPipe.transform(value, 0)
      : this.currencyPipe.transform(value, 2);

  /** Abre a janela na DRE; sem documento lido da DRE, no Balanço. */
  open(): void {
    this.statement.set(this.canCompareIncome() ? 'income' : 'balance');
    this.opened.set(true);
    this.dialogRef?.open('', '', '', '', '', 'statement-comparison', false, true);
  }

  show(statement: ComparedStatement): void {
    this.statement.set(statement);
  }
}
